import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { flappyAPI, platformAPI } from '../services/api';
import { buildInviteLink } from '../utils/inviteLinks';
import '../styles/Dashboard.css';

const CREATE_KIND_FRIENDLY = 'friendly';
const CREATE_KIND_REAL = 'real';
const QUICK_BOT_NAMES = ['SkyWing', 'PipeHunter', 'TurboBird'];

const DURATION_OPTIONS = [
  { seconds: 0, label: 'No Limit', icon: '∞', description: 'Unlimited time' },
  { seconds: 30, label: '30 sec', icon: '⚡', description: 'Quick reflexes' },
  { seconds: 45, label: '45 sec', icon: '🔥', description: 'Fast-paced' },
  { seconds: 60, label: '60 sec', icon: '🎯', description: 'Classic' },
  { seconds: 120, label: '2 min', icon: '⏱️', description: 'Endurance' },
];

export default function FlappyLobby() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  const [openMatches, setOpenMatches] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [ongoingMatches, setOngoingMatches] = useState([]);
  const [friends, setFriends] = useState([]);
  const [friendsOnlineStatus, setFriendsOnlineStatus] = useState({});

  const [wager, setWager] = useState(50);
  const [minimumWager, setMinimumWager] = useState(50);
  const [opponentId, setOpponentId] = useState('');
  const [durationSeconds, setDurationSeconds] = useState(60);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createKind, setCreateKind] = useState(CREATE_KIND_FRIENDLY);
  const [activeTab, setActiveTab] = useState('my');
  const [roomSearch, setRoomSearch] = useState('');
  const [quickSearching, setQuickSearching] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const roundsTotal = 1;
  const userId = Number(user?.id || 0);

  useEffect(() => {
    const loadPlatformSettings = async () => {
      try {
        const res = await platformAPI.getSettings();
        const nextMin = Number(res?.data?.minimumWager);
        if (Number.isFinite(nextMin) && nextMin >= 0) {
          setMinimumWager(nextMin);
          setWager((prev) => Math.max(nextMin, Number(prev) || 0));
        }
      } catch {
        // ignore settings failure
      }
    };

    loadPlatformSettings();
  }, []);

  const allMatches = useMemo(() => {
    const map = new Map();
    [...openMatches, ...challenges, ...ongoingMatches].forEach((m) => {
      const id = String(m?.id || '');
      if (!id) return;
      if (!map.has(id)) map.set(id, m);
    });

    const list = Array.from(map.values());
    list.sort((a, b) => {
      const ta = Date.parse(a?.created_at || '') || Number(a?.id || 0);
      const tb = Date.parse(b?.created_at || '') || Number(b?.id || 0);
      return tb - ta;
    });
    return list;
  }, [openMatches, challenges, ongoingMatches]);

  const myCreatedMatches = useMemo(
    () => allMatches.filter((m) => Number(m.creator_id) === userId),
    [allMatches, userId]
  );

  const myJoinedMatches = useMemo(
    () => allMatches.filter((m) => Number(m.opponent_id) === userId && Number(m.creator_id) !== userId),
    [allMatches, userId]
  );

  const availableOpenMatches = useMemo(() => {
    const query = String(roomSearch || '').trim().toLowerCase();
    let filtered = openMatches.filter((m) => Number(m.creator_id) !== userId);
    
    if (query) {
      filtered = filtered.filter((m) => {
        const id = String(m?.id || '').toLowerCase();
        const creator = String(m?.creator_username || '').toLowerCase();
        return id.includes(query) || creator.includes(query);
      });
    }
    
    return filtered;
  }, [openMatches, userId, roomSearch]);

  const canCreate = useMemo(() => {
    const w = Number(wager);
    if (!Number.isFinite(w) || w < 0) return false;
    if (createKind === CREATE_KIND_REAL && w < minimumWager) return false;
    return (user?.balance ?? 0) >= w;
  }, [wager, user, createKind, minimumWager]);

  const loadOpen = async () => {
    try {
      setError('');
      const res = await flappyAPI.listOpenMatches();
      setOpenMatches(res.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load open matches');
    } finally {
      setLoading(false);
    }
  };

  const loadChallenges = async () => {
    try {
      const res = await flappyAPI.listChallenges();
      setChallenges(res.data || []);
    } catch {
      // non-fatal
    }
  };

  const loadOngoing = async () => {
    try {
      const res = await flappyAPI.listOngoingMatches();
      setOngoingMatches(res.data || []);
    } catch {
      setOngoingMatches([]);
    }
  };

  const loadFriends = async () => {
    try {
      const res = await api.get('/friends/online-status');
      setFriends(res.data || []);
    } catch {
      try {
        const res = await api.get('/friends');
        setFriends(res.data || []);
      } catch {
        // non-fatal
      }
    }
  };

  useEffect(() => {
    const loadAll = async () => {
      await Promise.allSettled([loadOpen(), loadChallenges(), loadOngoing(), loadFriends()]);
    };

    loadAll();
    const id = setInterval(() => {
      void loadOpen();
      void loadChallenges();
      void loadOngoing();
    }, 2500);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refreshUser?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleQuickSearch = async () => {
    if (!userId) return;
    try {
      setQuickSearching(true);
      setError('');

      const openRes = await flappyAPI.listOpenMatches();
      const openMatchesList = Array.isArray(openRes.data) ? openRes.data : [];
      const candidate = openMatchesList.find((m) => Number(m.creator_id) !== userId && !m.opponent_id);

      if (candidate) {
        await flappyAPI.joinMatch(candidate.id);
        navigate(`/flappy/duel/${candidate.id}`);
        return;
      }

      const ownOpen = openMatchesList.find((m) => Number(m.creator_id) === userId && !m.opponent_id);
      const created = ownOpen || (await flappyAPI.createMatch({ wager: 0, roundsTotal: 1, durationSeconds: 60 })).data;
      const matchId = created?.id;

      if (matchId) {
        const waitMs = 7000;
        const pollMs = 1000;
        const startAt = Date.now();
        let opponentJoined = false;

        while (Date.now() - startAt < waitMs) {
          await new Promise((resolve) => setTimeout(resolve, pollMs));
          const matchRes = await flappyAPI.getMatch(matchId);
          if (matchRes?.data?.opponent_id) {
            opponentJoined = true;
            break;
          }
        }

        if (opponentJoined) {
          navigate(`/flappy/duel/${matchId}`);
          return;
        }

        try {
          await flappyAPI.cancelMatch(matchId);
        } catch {
          // ignore cancel failures for quick-search fallback
        }
      }

      const botName = QUICK_BOT_NAMES[Math.floor(Math.random() * QUICK_BOT_NAMES.length)] || 'SkyWing';
      navigate(`/flappy/practice?botName=${encodeURIComponent(botName)}`);
    } catch (e) {
      setError(e.response?.data?.error || 'Quick search failed');
    } finally {
      setQuickSearching(false);
    }
  };

  const shareMatchLink = async (matchId, roomType = 'challenge') => {
    if (!matchId) return;
    const link = buildInviteLink(`/flappy/duel/${matchId}`);
    const message = `Join my Flappy ${roomType}: ${link}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Flappy Invite', text: message, url: link });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        window.prompt('Share this Flappy invite link:', link);
      }
    } catch {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(link);
        } else {
          window.prompt('Share this Flappy invite link:', link);
        }
      } catch {
        window.prompt('Share this Flappy invite link:', link);
      }
    }
  };

  const handleCreate = async (kind = createKind, opts = {}) => {
    try {
      setError('');
      const w = kind === CREATE_KIND_REAL ? Math.max(0, Number(wager || 0)) : 0;
      if (kind === CREATE_KIND_REAL && w < minimumWager) {
        setError(`Wager must be at least ${minimumWager} credits`);
        return false;
      }
      const opp = String(opponentId || '').trim();
      const safeOpponentId = opp ? Number(opp) : null;
      const res = await flappyAPI.createMatch({ 
        wager: w, 
        roundsTotal, 
        durationSeconds, 
        opponentId: safeOpponentId 
      });
      const shouldShare = Boolean(opts.shareAfterCreate);
      if (shouldShare) {
        await shareMatchLink(res.data.id, kind === CREATE_KIND_REAL ? 'bet room' : 'friendly room');
      }
      refreshUser?.();
      navigate(`/flappy/duel/${res.data.id}`);
      return true;
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to create match');
      return false;
    }
  };

  const handleJoin = async (matchId) => {
    try {
      setError('');
      const res = await flappyAPI.joinMatch(matchId);
      refreshUser?.();
      navigate(`/flappy/duel/${res.data.id}`);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to join match');
    }
  };

  const handleAccept = async (matchId) => {
    try {
      setError('');
      await flappyAPI.acceptMatch(matchId);
      await Promise.allSettled([loadOpen(), loadChallenges(), loadOngoing()]);
      refreshUser?.();
      navigate(`/flappy/duel/${matchId}`);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to accept');
    }
  };

  const handleCancel = async (matchId) => {
    try {
      setError('');
      await flappyAPI.cancelMatch(matchId);
      refreshUser?.();
      await Promise.allSettled([loadOpen(), loadChallenges(), loadOngoing()]);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to cancel match');
    }
  };

  const getMatchFlags = (m) => {
    const creatorId = Number(m?.creator_id || 0);
    const opponentIdValue = Number(m?.opponent_id || 0);
    const status = String(m?.status || 'pending');

    const isCreator = creatorId === userId;
    const isOpponent = opponentIdValue === userId;
    const isParticipant = isCreator || isOpponent;
    const isOpen = !opponentIdValue;
    const isPending = status === 'pending';
    const isOngoing = status === 'in_progress';
    const isAccepted = Boolean(m?.accepted_at);

    const canCancel = isCreator && isPending && !m?.started_at;
    const canAccept = isOpponent && isPending && !isAccepted;
    const canJoin = isPending && isOpen && !isCreator && !isAccepted;
    const canOpen = isParticipant && isPending;
    const canContinue = isParticipant && isOngoing;

    return { isOngoing, canCancel, canAccept, canJoin, canOpen, canContinue };
  };

  const getStatusBadge = (status, isOngoing) => {
    if (isOngoing) return <span className="status-badge ongoing">In Progress</span>;
    if (status === 'pending') return <span className="status-badge pending">Waiting</span>;
    return <span className="status-badge completed">Completed</span>;
  };

  const getDurationLabel = (seconds) => {
    if (seconds === 0) return 'No limit';
    if (seconds < 60) return `${seconds} sec`;
    const mins = Math.floor(seconds / 60);
    const remainingSecs = seconds % 60;
    if (remainingSecs === 0) return `${mins} min`;
    return `${mins} min ${remainingSecs} sec`;
  };

  return (
    <div className="dashboard-container dashboard-redesign">
      <div className="animated-bg">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      <div className="dashboard-content">
        {/* Header */}
        <div className="chess-lobby-header">
          <div className="header-left">
            <div className="header-icon flappy-icon">🐦</div>
            <div>
              <h1 className="page-title">Flappy Arena</h1>
              <p className="page-subtitle">Quick reflex duels • Challenge friends or play for credits</p>
            </div>
          </div>
          <button className="back-btn" onClick={() => navigate('/dashboard')}>
            ← Back to Dashboard
          </button>
        </div>

        {/* User Stats Card */}
        <div className="user-stats-card">
          <div className="stats-row">
            <div className="stat-item">
              <div className="stat-icon">💰</div>
              <div className="stat-info">
                <div className="stat-label">Balance</div>
                <div className="stat-value">{user?.balance ?? 0} credits</div>
              </div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-icon">⏱️</div>
              <div className="stat-info">
                <div className="stat-label">Default Duration</div>
                <div className="stat-value">{getDurationLabel(durationSeconds)}</div>
              </div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-icon">🎮</div>
              <div className="stat-info">
                <div className="stat-label">Active Games</div>
                <div className="stat-value">
                  {myCreatedMatches.filter((m) => m.status === 'in_progress').length + 
                   myJoinedMatches.filter((m) => m.status === 'in_progress').length}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="error-alert">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
            <button className="error-close" onClick={() => setError('')}>×</button>
          </div>
        )}

        {/* Quick Actions */}
        <div className="quick-actions-card">
          <div className="quick-actions-row">
            <button 
              className={`quick-action flappy-quick ${quickSearching ? 'searching' : ''}`}
              disabled={!userId || quickSearching}
              onClick={handleQuickSearch}
            >
              {quickSearching ? (
                <>
                  <span className="spinner-small"></span>
                  Searching...
                </>
              ) : (
                <>
                  <span className="action-icon">⚡</span>
                  Quick Match
                </>
              )}
            </button>
            
            <button 
              className="practice-btn"
              onClick={() => navigate('/flappy/practice')}
            >
              🎮 Practice Mode
            </button>
          </div>
        </div>

        {/* Create Challenge Cards */}
        <div className="create-challenges">
          <div className="section-header">
            <h2 className="section-title">Create Challenge</h2>
            <p className="section-subtitle">Choose your game mode</p>
          </div>
          
          <div className="create-cards">
            <div className="create-card practice" onClick={() => navigate('/flappy/practice')}>
              <div className="create-icon">🎯</div>
              <div className="create-info">
                <div className="create-title">Practice Mode</div>
                <div className="create-desc">Warm up without wager</div>
              </div>
              <div className="create-arrow">→</div>
            </div>
            
            <div className="create-card friendly" onClick={() => { 
              setCreateKind(CREATE_KIND_FRIENDLY); 
              setShowCreateModal(true); 
            }}>
              <div className="create-icon">👥</div>
              <div className="create-info">
                <div className="create-title">Friendly Challenge</div>
                <div className="create-desc">No wager, invite or open room</div>
              </div>
              <div className="create-arrow">→</div>
            </div>
            
            <div className="create-card real" onClick={() => { 
              setCreateKind(CREATE_KIND_REAL); 
              setShowCreateModal(true); 
            }}>
              <div className="create-icon">💰</div>
              <div className="create-info">
                <div className="create-title">Real Challenge</div>
                <div className="create-desc">Compete for credits</div>
              </div>
              <div className="create-arrow">→</div>
            </div>
          </div>
        </div>

        {/* Matches Section */}
        <div className="matches-section">
          <div className="matches-tabs">
            <button 
              className={`tab-btn ${activeTab === 'my' ? 'active' : ''}`} 
              onClick={() => setActiveTab('my')}
            >
              <span className="tab-icon">🎯</span>
              My Games
              <span className="tab-count">{myCreatedMatches.length + myJoinedMatches.length}</span>
            </button>
            <button 
              className={`tab-btn ${activeTab === 'available' ? 'active' : ''}`} 
              onClick={() => setActiveTab('available')}
            >
              <span className="tab-icon">🌍</span>
              Available
              <span className="tab-count">{availableOpenMatches.length}</span>
            </button>
          </div>

          {loading && (
            <div className="loading-state">
              <div className="spinner"></div>
              <span>Loading matches...</span>
            </div>
          )}

          {!loading && activeTab === 'available' && (
            <>
              <div className="search-bar">
                <span className="search-icon">🔍</span>
                <input
                  className="search-input"
                  value={roomSearch}
                  onChange={(e) => setRoomSearch(e.target.value)}
                  placeholder="Search by match ID or player name..."
                />
                {roomSearch && (
                  <button className="clear-search" onClick={() => setRoomSearch('')}>×</button>
                )}
              </div>

              {availableOpenMatches.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">🐦</div>
                  <div className="empty-title">No available challenges</div>
                  <div className="empty-desc">
                    {roomSearch ? `No matches matching "${roomSearch}"` : 'Create a challenge to start playing'}
                  </div>
                  {roomSearch && (
                    <button className="clear-filters-btn" onClick={() => setRoomSearch('')}>
                      Clear Search
                    </button>
                  )}
                </div>
              ) : (
                <div className="matches-list">
                  {availableOpenMatches.map((m) => {
                    const isBetRoom = Number(m.wager || 0) > 0;
                    return (
                      <div key={`available-${m.id}`} className="match-card available">
                        <div className="match-header">
                          <div className="match-id">Match #{m.id}</div>
                          <div className="match-badges">
                            <span className={`mode-badge ${isBetRoom ? 'real' : 'friendly'}`}>
                              {isBetRoom ? '💰 Real' : '🎮 Friendly'}
                            </span>
                            {isBetRoom && (
                              <span className="wager-badge">Wager: {m.wager}</span>
                            )}
                          </div>
                        </div>
                        <div className="match-players">
                          <div className="player">
                            <span className="player-role">Host</span>
                            <span className="player-name">{m.creator_username || 'Unknown'}</span>
                          </div>
                          <div className="vs-divider">VS</div>
                          <div className="player">
                            <span className="player-role">Open</span>
                            <span className="player-name waiting">Waiting for opponent</span>
                          </div>
                        </div>
                        <div className="match-details">
                          <span className="detail-item">⏱️ Duration: {getDurationLabel(m.duration_seconds)}</span>
                          <span className="detail-item">🎯 Rounds: {m.rounds_total || 1}</span>
                          <span className="detail-item">📅 {new Date(m.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="match-actions">
                          <button className="action-btn join" onClick={() => handleJoin(m.id)}>
                            Join Challenge →
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {!loading && activeTab === 'my' && (
            <>
              {[...myCreatedMatches, ...myJoinedMatches].length === 0 && (
                <div className="empty-state">
                  <div className="empty-icon">🎮</div>
                  <div className="empty-title">No active games</div>
                  <div className="empty-desc">Create a challenge or join an available game to start playing</div>
                </div>
              )}

              <div className="matches-list">
                {[...myCreatedMatches, ...myJoinedMatches].map((m) => {
                  const flags = getMatchFlags(m);
                  const isBetRoom = Number(m.wager || 0) > 0;
                  const isCreatedByMe = Number(m.creator_id) === userId;
                  return (
                    <div key={`mine-${m.id}`} className="match-card">
                      <div className="match-header">
                        <div className="match-id">Match #{m.id}</div>
                        <div className="match-badges">
                          <span className={`mode-badge ${isBetRoom ? 'real' : 'friendly'}`}>
                            {isBetRoom ? '💰 Real' : '🎮 Friendly'}
                          </span>
                          {isBetRoom && (
                            <span className="wager-badge">Wager: {m.wager}</span>
                          )}
                          {getStatusBadge(m.status, flags.isOngoing)}
                        </div>
                      </div>
                      <div className="match-players">
                        <div className="player">
                          <span className="player-role">
                            {Number(m.creator_id) === userId ? 'You (Creator)' : 'Host'}
                          </span>
                          <span className="player-name">{m.creator_username || '-'}</span>
                        </div>
                        <div className="vs-divider">VS</div>
                        <div className="player">
                          <span className="player-role">
                            {Number(m.opponent_id) === userId ? 'You' : 'Opponent'}
                          </span>
                          <span className="player-name">
                            {m.opponent_username || (m.opponent_id ? 'Player' : 'Waiting...')}
                          </span>
                        </div>
                      </div>
                      <div className="match-details">
                        <span className="detail-item">⏱️ Duration: {getDurationLabel(m.duration_seconds)}</span>
                        <span className="detail-item">🎯 Rounds: {m.rounds_total || 1}</span>
                        <span className="detail-item">📅 {new Date(m.created_at).toLocaleDateString()}</span>
                      </div>
                      {isCreatedByMe && (
                        <div className="match-invite-row">
                          <button
                            className="action-btn watch"
                            onClick={() => shareMatchLink(m.id, isBetRoom ? 'bet room' : 'friendly room')}
                            title="Copy invite link"
                            aria-label="Copy invite link"
                          >
                            📋
                          </button>
                        </div>
                      )}
                      <div className="match-actions">
                        {flags.canAccept && (
                          <button className="action-btn join" onClick={() => handleAccept(m.id)}>
                            Accept & Play →
                          </button>
                        )}
                        {flags.canJoin && (
                          <button className="action-btn join" onClick={() => handleJoin(m.id)}>
                            Join →
                          </button>
                        )}
                        {(flags.canOpen || flags.canContinue) && (
                          <button className="action-btn play" onClick={() => navigate(`/flappy/duel/${m.id}`)}>
                            {flags.canContinue ? 'Continue →' : 'Open →'}
                          </button>
                        )}
                        {flags.canCancel && (
                          <button className="action-btn cancel" onClick={() => handleCancel(m.id)}>
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Create Modal */}
        {showCreateModal && (
          <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
            <div className="modal-container chess-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-icon">🐦</div>
                <h3 className="modal-title">
                  {createKind === CREATE_KIND_REAL ? 'Real Challenge' : 'Friendly Challenge'}
                </h3>
                <button className="modal-close" onClick={() => setShowCreateModal(false)}>×</button>
              </div>
              
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Duration</label>
                  <div className="time-options">
                    {DURATION_OPTIONS.map((opt) => (
                      <button
                        key={opt.seconds}
                        className={`time-option ${durationSeconds === opt.seconds ? 'selected' : ''}`}
                        onClick={() => setDurationSeconds(opt.seconds)}
                      >
                        <span className="time-icon">{opt.icon}</span>
                        <span className="time-label">{opt.label}</span>
                        <span className="time-desc">{opt.description}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {createKind === CREATE_KIND_REAL && (
                  <div className="form-group">
                    <label className="form-label">Wager Amount</label>
                    <div className="wager-input-wrapper">
                      <span className="wager-currency">💰</span>
                      <input
                        type="number"
                        className="form-input wager-input"
                        min={minimumWager}
                        max={user?.balance ?? 0}
                        value={wager}
                        onChange={(e) => setWager(Number(e.target.value))}
                      />
                      <span className="wager-suffix">credits</span>
                    </div>
                    <div className="form-hint">
                      Minimum: {minimumWager} credits • Balance: {user?.balance ?? 0} credits
                    </div>
                    {!canCreate && (
                      <div className="error-hint">
                        {Number(wager) < minimumWager
                          ? `⚠️ Minimum wager is ${minimumWager} credits`
                          : '⚠️ Insufficient balance for this wager'}
                      </div>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Invite Friend (Optional)</label>
                  <select 
                    className="form-select" 
                    value={opponentId} 
                    onChange={(e) => setOpponentId(e.target.value)}
                  >
                    <option value="">Open match (anyone can join)</option>
                    {friends.map((f) => (
                      <option key={f.friendship_id || f.friend_id} value={f.friend_id}>
  {Boolean(friendsOnlineStatus[f.friend_id]) ? '🟢' : '⚪'} | {f.friend_username || f.username || 'Friend'}
</option>
                    ))}
                  </select>
                  <div className="form-hint">Send a direct challenge to a specific friend</div>
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  className="btn-create" 
                  onClick={async () => {
                    const created = await handleCreate(createKind);
                    if (created) setShowCreateModal(false);
                  }} 
                  disabled={createKind === CREATE_KIND_REAL && !canCreate}
                >
                  Create Challenge →
                </button>
                <button
                  className="join-btn"
                  onClick={async () => {
                    const created = await handleCreate(createKind, { shareAfterCreate: true });
                    if (created) setShowCreateModal(false);
                  }}
                  disabled={createKind === CREATE_KIND_REAL && !canCreate}
                >
                  Create & Copy Link
                </button>
                <button className="btn-cancel" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
